import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './product/app-shell'
import { useAuth } from './product/auth'
import {
  LoginPage,
  PasswordPage,
  SignupPage,
  VerifyEmailPage,
} from './product/auth-pages'
import { LayoutEditorPage, LayoutsPage } from './product/content-pages'
import { DashboardPage } from './product/dashboard-page'
import { DevicesPage, GroupsPage } from './product/fleet-pages'
import {
  AnalyticsPage,
  AuditPage,
  NotificationsPage,
} from './product/insight-pages'
import { InvitationPage } from './product/invitation-page'
import { MediaPage } from './product/media-page'
import {
  BillingPage,
  BrandingPage,
  MfaPage,
  TeamPage,
} from './product/organization-pages'
import { OverlaysPage } from './product/overlay-page'
import {
  PlatformEmailGatewayPage,
  PlatformHealthPage,
  PlatformPlansPage,
  PlatformReleasesPage,
  PlatformTenantsPage,
} from './product/platform-pages'
import { PlaylistsPage, TickersPage } from './product/playlist-pages'
import { PublishPage, SchedulesPage } from './product/schedule-pages'
import type { TenantRole } from './product/types'
import { LoadingState } from './product/ui'

const contentRoles: TenantRole[] = [
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'CONTENT_MANAGER',
]
const deviceRoles: TenantRole[] = [
  'TENANT_OWNER',
  'TENANT_ADMIN',
  'DEVICE_MANAGER',
]
const adminRoles: TenantRole[] = ['TENANT_OWNER', 'TENANT_ADMIN']

function RequireAuth() {
  const { session, loading } = useAuth()
  const location = useLocation()
  if (loading) {
    return (
      <div className='grid min-h-svh place-items-center'>
        <LoadingState label='Restoring secure session' />
      </div>
    )
  }
  if (session?.mfaEnrollmentRequired && location.pathname !== '/app/security') {
    return <Navigate to='/app/security' replace />
  }
  return session ? <AppShell /> : <Navigate to='/login' replace />
}

function RequirePlatform({ children }: { children: React.ReactNode }) {
  const { session } = useAuth()
  return session?.user.platformAdmin ? children : <Navigate to='/app' replace />
}

function RequireRole({
  roles,
  children,
}: {
  roles: TenantRole[]
  children: React.ReactNode
}) {
  const { role } = useAuth()
  return role && roles.includes(role) ? (
    children
  ) : (
    <Navigate to='/app' replace />
  )
}

export default function App() {
  return (
    <Routes>
      <Route path='/login' element={<LoginPage />} />
      <Route path='/signup' element={<SignupPage />} />
      <Route path='/verify-email' element={<VerifyEmailPage />} />
      <Route path='/forgot-password' element={<PasswordPage />} />
      <Route path='/reset-password' element={<PasswordPage reset />} />
      <Route path='/accept-invitation' element={<InvitationPage />} />
      <Route path='/app' element={<RequireAuth />}>
        <Route index element={<DashboardPage />} />
        <Route
          path='media'
          element={
            <RequireRole roles={contentRoles}>
              <MediaPage />
            </RequireRole>
          }
        />
        <Route
          path='slides/new'
          element={
            <RequireRole roles={contentRoles}>
              <LayoutEditorPage />
            </RequireRole>
          }
        />
        <Route
          path='slides/:layoutId/edit'
          element={
            <RequireRole roles={contentRoles}>
              <LayoutEditorPage />
            </RequireRole>
          }
        />
        <Route
          path='slides'
          element={
            <RequireRole roles={contentRoles}>
              <LayoutsPage />
            </RequireRole>
          }
        />
        <Route path='layouts' element={<Navigate to='/app/slides' replace />} />
        <Route
          path='overlays'
          element={
            <RequireRole roles={contentRoles}>
              <OverlaysPage />
            </RequireRole>
          }
        />{' '}
        <Route
          path='tickers'
          element={
            <RequireRole roles={contentRoles}>
              <TickersPage />
            </RequireRole>
          }
        />
        <Route
          path='playlists'
          element={
            <RequireRole roles={contentRoles}>
              <PlaylistsPage />
            </RequireRole>
          }
        />
        <Route
          path='schedules'
          element={
            <RequireRole roles={contentRoles}>
              <SchedulesPage />
            </RequireRole>
          }
        />
        <Route
          path='publish'
          element={
            <RequireRole roles={contentRoles}>
              <PublishPage />
            </RequireRole>
          }
        />
        <Route
          path='devices'
          element={
            <RequireRole roles={deviceRoles}>
              <DevicesPage />
            </RequireRole>
          }
        />
        <Route
          path='groups'
          element={
            <RequireRole roles={deviceRoles}>
              <GroupsPage />
            </RequireRole>
          }
        />
        <Route path='analytics' element={<AnalyticsPage />} />
        <Route
          path='team'
          element={
            <RequireRole roles={adminRoles}>
              <TeamPage />
            </RequireRole>
          }
        />
        <Route
          path='billing'
          element={
            <RequireRole roles={['TENANT_OWNER']}>
              <BillingPage />
            </RequireRole>
          }
        />
        <Route
          path='branding'
          element={
            <RequireRole roles={adminRoles}>
              <BrandingPage />
            </RequireRole>
          }
        />
        <Route path='security' element={<MfaPage />} />
        <Route path='notifications' element={<NotificationsPage />} />
        <Route path='audit' element={<AuditPage />} />
        <Route
          path='platform/tenants'
          element={
            <RequirePlatform>
              <PlatformTenantsPage />
            </RequirePlatform>
          }
        />
        <Route
          path='platform/plans'
          element={
            <RequirePlatform>
              <PlatformPlansPage />
            </RequirePlatform>
          }
        />
        <Route
          path='platform/email-gateway'
          element={
            <RequirePlatform>
              <PlatformEmailGatewayPage />
            </RequirePlatform>
          }
        />
        <Route
          path='platform/releases'
          element={
            <RequirePlatform>
              <PlatformReleasesPage />
            </RequirePlatform>
          }
        />
        <Route
          path='platform/health'
          element={
            <RequirePlatform>
              <PlatformHealthPage />
            </RequirePlatform>
          }
        />
      </Route>
      <Route path='/' element={<Navigate to='/app' replace />} />
      <Route path='*' element={<Navigate to='/app' replace />} />
    </Routes>
  )
}
